/**
 * Score global de chaque candidat, par test, pour la table Candidats.
 *
 * Mêmes moteurs que les onglets Matching et la fiche candidat
 * (`useMatchingReferentiel`, `useClassementEntStruct`) : un score lu dans la
 * table est celui que la fiche détaille.
 */
import { useMemo } from "react";
import type { EvaluationData } from "@repo/results";
import type { Id } from "../../../convex/_generated/dataModel";
import type { ResultatComparaison } from "../../../convex/referentiel/comparer";
import { useClassementEntStruct } from "./entstruct/useClassementEntStruct";
import {
  useMatchingReferentiel,
  type EvaluationFitCulturelRow,
  type EvaluationPamiRow,
} from "./referentiel/useMatchingReferentiel";

/** Références stables : un `[]` littéral relancerait le calcul à chaque rendu. */
const AUCUNE_PAMI: EvaluationPamiRow[] = [];
const AUCUNE_FIT_CULTUREL: EvaluationFitCulturelRow[] = [];

export type ScoresCandidats = {
  /** Comparaison au référentiel poste, par `parcoursId`. */
  pami: Map<string, ResultatComparaison | null>;
  /** Comparaison au référentiel culture (r), par `parcoursId`. */
  fitculturel: Map<string, ResultatComparaison | null>;
  /** Couverture pondérée du barème FairView, sur 100, par `parcoursId`. */
  entstruct: Map<string, number | null>;
};

/**
 * Comparaison au référentiel d'une cible, par `parcoursId`, contre la DERNIÈRE
 * version : la seule contre laquelle tous les candidats sont comparables. Map
 * vide tant que le référentiel n'existe pas ou n'a aucune version.
 */
function useScoresMatching(
  campagneId: Id<"campagnes">,
  cible: "pami" | "pc-fit",
  actif: boolean,
  evaluationsPami: EvaluationPamiRow[] | undefined,
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined,
): Map<string, ResultatComparaison | null> {
  // Une cible ne compare QUE ses passations : lui passer l'autre jeu ferait
  // cohabiter des candidats notés sur des échelles différentes.
  const { classement } = useMatchingReferentiel({
    campagneId,
    cible,
    actif,
    evaluationsPami: cible === "pami" ? evaluationsPami : AUCUNE_PAMI,
    evaluationsFitCulturel:
      cible === "pc-fit" ? evaluationsFitCulturel : AUCUNE_FIT_CULTUREL,
  });
  return useMemo(
    () =>
      new Map(classement.map((e) => [e.row.parcoursId, e.comparaison] as const)),
    [classement],
  );
}

export function useScoresCandidats({
  campagneId,
  evaluationsEntStruct,
  evaluationsPami,
  evaluationsFitCulturel,
  aPami,
  aFitCulturel,
  aEntStruct,
}: {
  campagneId: Id<"campagnes">;
  evaluationsEntStruct: EvaluationData[] | undefined;
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  aPami: boolean;
  aFitCulturel: boolean;
  aEntStruct: boolean;
}): ScoresCandidats {
  const pami = useScoresMatching(
    campagneId,
    "pami",
    aPami,
    evaluationsPami,
    undefined,
  );
  const fitculturel = useScoresMatching(
    campagneId,
    "pc-fit",
    aFitCulturel,
    undefined,
    evaluationsFitCulturel,
  );
  // Ce n'est PAS un matching à un référentiel : l'entretien structuré n'a pas
  // de référents, son étalon est le barème de son template.
  const { classement } = useClassementEntStruct(
    aEntStruct ? evaluationsEntStruct : undefined,
  );
  const entstruct = useMemo(
    () =>
      new Map(
        classement.map((e) => [e.evaluation.parcoursId, e.score] as const),
      ),
    [classement],
  );
  return useMemo(
    () => ({ pami, fitculturel, entstruct }),
    [pami, fitculturel, entstruct],
  );
}
